import { ApolloClient, HttpLink, InMemoryCache } from "@apollo/client/core";
import { signal } from "@preact-signals/safe-react";
import * as Sentry from "@sentry/react";
import { PARAMS } from "@src/common/constants";
import config from "@src/config";
import { isAbortError, isOfflineNetworkError } from "@src/utils/error-handling";
import { auth0Client } from "./auth0-provider-with-navigate";
import {
	loginInteractively,
	shouldUseDesktopBrowserLogin,
} from "./desktop-browser-login";
import {
	guestWebSocketTokenQuery,
	guestWebSocketTokenQuerySchema,
} from "./queries/guest-websocket-token";

export const accessTokenSignal = signal<string | null>(null);
export const shareKeySignal = signal<string | null>(null);
export const websocketGuestWhiteboardIdSignal = signal<string | null>(null);

export function isAuthenticated() {
	return accessTokenSignal.value !== null;
}

// Function to setup asset authentication with HTTP-only cookie
let lastAccessToken: string | null | undefined = undefined;
let lastShareKey: string | null | undefined = undefined;
let cachedGuestWebSocketToken: string | null = null;
let cachedGuestWebSocketTokenExpiresAt = 0;
let cachedGuestWebSocketTokenKey: string | null = null;

// Standalone client so this module never imports getApiClient() / authLink — avoids
// circular dependency while fetching a guest WS token before the main client runs.
const guestWebSocketTokenClient = new ApolloClient({
	link: new HttpLink({
		uri: config.graphqlUrl,
		credentials: "include",
	}),
	cache: new InMemoryCache(),
});

export const setupAssetAuth = async (
	accessToken: string | null,
	shareKey: string | null,
) => {
	// Validate that at least one authentication method is provided
	if (!accessToken && !shareKey) {
		throw new Error(
			"To fetch asset cookie specify accessToken and/or shareKey.",
		);
	}

	// Only proceed if accessToken or shareKey has changed since last call
	if (accessToken === lastAccessToken && shareKey === lastShareKey) {
		return; // No change, skip fetch
	}

	lastAccessToken = accessToken;
	lastShareKey = shareKey;

	try {
		// Prepare URL and add shareKey as query param if available
		let authUrl = `${config.restApiUrl}/v1/assets/auth`;
		if (shareKey) {
			authUrl += `?shareKey=${encodeURIComponent(shareKey)}`;
		}

		// Prepare headers
		const headers: Record<string, string> = {};
		if (accessToken) {
			headers.Authorization = `Bearer ${accessToken}`;
		}

		// Call the asset auth endpoint to set the HTTP-only cookie
		await fetch(authUrl, {
			method: "POST",
			headers,
			credentials: "include", // Important for cookies
		});
	} catch (err) {
		// Soft-fail: callers continue; assets may 401 until a later retry.
		lastAccessToken = null;
		lastShareKey = null;
		console.warn("Failed to set asset authentication cookie:", err);
		if (!isAbortError(err) && !isOfflineNetworkError(err)) {
			Sentry.captureException(err);
		}
	}
};

export function getShareKey(searchParams?: string) {
	const params = new URLSearchParams(searchParams ?? window.location.search);
	shareKeySignal.value = params.get(PARAMS.shareKey) ?? null;
	return shareKeySignal.value;
}

export async function getAccessToken() {
	getShareKey();

	try {
		accessTokenSignal.value = await auth0Client.getTokenSilently();
	} catch (error) {
		if (!shareKeySignal.value) {
			// Desktop: never auto-open the system browser from API calls —
			// Sign in must be user-initiated.
			if (!shouldUseDesktopBrowserLogin()) {
				await loginInteractively({
					appState: {
						returnTo: `${window.location.pathname}${window.location.search}`,
					},
				});
			}
			throw error;
		}
	}

	// setupAssetAuth is a network call to our backend — if it fails the user
	// is still authenticated, don't redirect to Auth0.
	try {
		await setupAssetAuth(accessTokenSignal.value, shareKeySignal.value);
	} catch {
		// Already logged + reported to Sentry inside setupAssetAuth
	}

	return accessTokenSignal.value;
}

export async function getGuestWebSocketToken() {
	shareKeySignal.value = getShareKey();

	const shareKey = shareKeySignal.value;
	const whiteboardId = websocketGuestWhiteboardIdSignal.value;

	if (!shareKey || !whiteboardId) {
		return null;
	}

	const cacheKey = `${whiteboardId}:${shareKey}`;
	if (
		cachedGuestWebSocketToken &&
		cachedGuestWebSocketTokenKey === cacheKey &&
		Date.now() < cachedGuestWebSocketTokenExpiresAt - 10_000
	) {
		return cachedGuestWebSocketToken;
	}

	const response = await guestWebSocketTokenClient.query({
		query: guestWebSocketTokenQuery,
		variables: {
			whiteboardId,
			shareKey,
		},
		fetchPolicy: "no-cache",
	});
	const payload = guestWebSocketTokenQuerySchema.parse(
		response.data,
	).guestWebSocketToken;

	cachedGuestWebSocketToken = payload.token;
	cachedGuestWebSocketTokenKey = cacheKey;
	cachedGuestWebSocketTokenExpiresAt = Date.parse(payload.expiresAt);

	return payload.token;
}
